import { Navigate, RouteObject } from 'react-router';
import { SearchFeature } from '@vivli/features/search/feature';
import { ResearchTeamMemberOrcidAcceptComponent, ViewDataPackageComponent } from '@vivli/features/studies/components';
import { DatasetFeature } from '@vivli/features/datasets/feature';
import {
    AttestationsFormComponent,
    DataRequestHistoryComponent,
    DataRequestNotificationsSubTabComponent,
    DataRequestPrintViewRenderComponent,
    DataRequestPublishApprovedComponent,
    DraftResearchTeamComponent,
    FundingFormComponent,
    OtherInformationFormComponent,
    PublishApprovedPreviewComponent,
    ResearchProposalFormComponent,
    StatisticalAnalysisPlanFormComponent,
    ViewSecondaryAnalysisContainerComponent,
    DataRequestAttachmentsComponent,
    DataRequestCitationsComponent,
    DataRequestPrintViewComponent,
    DataRequestResearchEnvironmentComponent,
    DataRequestResearchTeamComponent,
    DataRequestSignedDocumentsComponent,
    DataRequestStatusUpdateComponent,
    DataRequestStudiesComponent,
    DisclosuresContainerComponent,
    ReportSafetyConcernComponent,
    AmrDataRequestDatasetsComponent,
    AmrDataRequestPrintViewComponent,
    AmrDataRequestViewComponent,
    AmrRequestFormComponent
} from '@vivli/features/data-requests/components';
import { ProtectedRoute } from '@vivli/core/wrapper';
import { PortalFeature } from '@vivli/features/portal/feature';
import {
    AmrDataRequestFeature,
    DataRequestDraftFeature,
    DataRequestByVivliIdFeature,
    DataRequestFeature,
    DataRequestsFeature,
    MyDataRequestsFeature
} from '@vivli/features/data-requests/feature';
import { ListingRequestFeature, ListingRequestsFeature } from '@vivli/features/listing-request/feature';
import { ManageVmsFeature, VirtualMachinePortalFeature } from '@vivli/features/virtual-machine/feature';
import { DisclosureFeature, DisclosureFromUrlFeature } from '@vivli/features/disclosures/feature';
import {
    AgreementsComponent,
    DataSharingSettingsComponent,
    InformationAboutYourTeamComponent,
    ListingLandingViewComponent,
    ListingRequestChatComponent,
    ListingRequestHistoryComponent,
    ListingStatusUpdateComponent,
    UploadDataComponent,
    YourOrganizationComponent,
    YourStudyComponent
} from '@vivli/features/listing-request/components';
import { EnquiriesEntryWrapper, EnquiryLandingViewComponent, EnquiryPortalComponent } from '@vivli/features/enquiry/components';
import { NotFoundComponent } from '@vivli/shared/components';
import { useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { LayoutWrapper, NoLayoutWrapper } from '@vivli/core/layout';
import { usePrintView } from '@vivli/shared/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { ResultRequestsFeature } from '@vivli/features/results-request/feature';
import { ChatRollupFeature } from '@vivli/features/chat/feature';
import {
    AdminStudyFeature,
    AnnotateStudyFeature,
    AwaitingUploadStudiesFeature,
    BulkUpdateStudiesFeature,
    BulkUploadStudiesFeature,
    RequestedStudiesFeature,
    StudiesFeature,
    SubmitStudyFeature
} from '@vivli/features/studies/feature';
import { PlatformInitWrapper } from '@vivli/core/infrastructure/wrappers';
import { DashboardFeatureRouteWrapper } from '@vivli/features/dashboard/infrastructure/wrappers';
import { OrganizationDetailsFeature, OrganizationsFeature } from '@vivli/features/organizations/feature';
import { UserDetailsFeature, UsersFeature } from '@vivli/features/users/feature';
import { ReportFeature } from '@vivli/features/dashboard/feature';
import { AdminConsoleRequestsFeature } from '@vivli/features/admin-console/feature';
import { EnquiresFeature, EnquiryFeature } from '@vivli/features/enquiry/feature';

export const useDefaultRoutes = (): RouteObject[] => {
    const { isAmr } = useAssignedAppTypeContext();
    const isPrintView = usePrintView();

    const dataRequestFeatureToUse = isAmr ? <AmrDataRequestFeature /> : <DataRequestFeature />;

    const dataRequestDraftToUse = isAmr ? <AmrDataRequestFeature /> : <DataRequestDraftFeature />;

    const layoutWrapperToUse = !isPrintView ? <LayoutWrapper /> : <NoLayoutWrapper />;

    const priorityRoutes = [
        {
            path: '/',
            element: <SearchFeature />
        },
        {
            path: '/portal/:customPortal',
            element: <SearchFeature />
        },
        {
            path: '/partner-login',
            element: <SearchFeature />
        },
        {
            path: '/mode/amr',
            element: <SearchFeature />
        },
        {
            path: '/mode/default',
            element: <SearchFeature />
        },
        {
            path: '/acceptInvite/:orgId',
            element: null
        },
        {
            path: '/acceptInviteForDataRequest/:dataRequestId/:teamMemberId',
            element: null
        },
        {
            path: '/orcid/:studyId/:orcidId',
            element: <ResearchTeamMemberOrcidAcceptComponent />
        }
    ];

    const publicRoutes = [
        {
            path: '/doiLanding',
            children: [
                {
                    path: 'studies/:studyId/:isLanding',
                    element: <DatasetFeature />
                },
                {
                    path: 'datasets/:studyId/:isLanding',
                    element: <DatasetFeature />
                },
                {
                    path: 'dataRequests/:dataRequestDoi',
                    element: <ViewSecondaryAnalysisContainerComponent />
                },
                {
                    path: 'dataPackages/:dataPackageDoi',
                    element: <ViewDataPackageComponent />
                }
            ]
        },
        {
            path: '/studyDetails',
            children: [
                {
                    path: 'fromSearch/:studyId',
                    element: <DatasetFeature />
                },
                {
                    path: ':studyId',
                    element: <DatasetFeature />
                }
            ]
        },
        {
            path: '/datasetDetails',
            children: [
                {
                    path: 'fromSearch/:studyId',
                    element: <DatasetFeature />
                },
                {
                    path: ':studyId',
                    element: <DatasetFeature />
                }
            ]
        }
    ];

    const datasetRoutes = [
        {
            path: '/datasetDetails',
            children: [
                {
                    path: ':studyId/:requestedStudyIpdDataPackageId/:dataRequestId/:reqStudyDl',
                    element: <DatasetFeature />
                },
                {
                    path: ':studyId/:requestedStudyIpdDataPackageId',
                    element: <DatasetFeature />
                }
            ]
        }
    ];

    const studyRoutes = [
        {
            path: '/studyDetails',
            children: [
                {
                    path: ':studyId/:requestedStudyIpdDataPackageId/:dataRequestId/:reqStudyDl',
                    element: <DatasetFeature />
                },
                {
                    path: ':studyId/:requestedStudyIpdDataPackageId/:dataRequestId',
                    element: <DatasetFeature />
                },
                {
                    path: ':studyId/:requestedStudyIpdDataPackageId',
                    element: <DatasetFeature />
                }
            ]
        }
    ];

    const dataRequestDraftRoutes = [
        {
            path: 'researchTeam',
            element: <DraftResearchTeamComponent />
        },
        {
            path: 'researchProposal',
            element: <ResearchProposalFormComponent />
        },
        {
            path: 'statisticalAnalysisPlan',
            element: <StatisticalAnalysisPlanFormComponent />
        },
        {
            path: 'funding',
            element: <FundingFormComponent />
        },
        {
            path: 'otherInformation',
            element: <OtherInformationFormComponent />
        },
        {
            path: 'attestations',
            element: <AttestationsFormComponent />
        },
        {
            path: 'requestedStudies',
            element: <RequestedStudiesFeature editable={true} />
        },
        {
            path: 'chat',
            element: <ChatRollupFeature />
        },
        {
            path: 'researchResults',
            element: <ResultRequestsFeature />
        },
        {
            path: 'requestHistory',
            element: <DataRequestHistoryComponent />
        },
        {
            path: 'Notifications',
            element: <DataRequestNotificationsSubTabComponent />
        }
    ];

    const amrDataRequestRoutes = [
        {
            path: 'Datasets',
            element: <AmrDataRequestDatasetsComponent />
        },
        {
            path: 'RequestHistory',
            element: <DataRequestHistoryComponent />
        },
        {
            index: true,
            path: 'Chat',
            element: <ChatRollupFeature />
        },
        {
            path: 'Request',
            element: <AmrRequestFormComponent />
        },
        {
            path: 'StatusUpdate',
            element: <DataRequestStatusUpdateComponent />
        },
        {
            path: 'PrintView',
            element: <AmrDataRequestPrintViewComponent />
        },
        {
            path: 'RequestPrintView',
            element: <AmrDataRequestViewComponent />
        }
    ];

    const dataRequestDraftPageRoutes = [
        {
            path: ':status/:requestId',
            element: dataRequestDraftToUse,
            children: !isAmr ? dataRequestDraftRoutes : amrDataRequestRoutes
        },
        {
            path: ':requestId',
            element: dataRequestDraftToUse,
            children: !isAmr ? dataRequestDraftRoutes : amrDataRequestRoutes
        }
    ];

    const ctDataRequestPageRoutes = [
        {
            path: 'ResearchTeam',
            element: <DataRequestResearchTeamComponent />
        },
        {
            path: 'Studies',
            element: <DataRequestStudiesComponent />
        },
        {
            path: 'RequestHistory',
            element: <DataRequestHistoryComponent />
        },
        {
            path: 'Attachments',
            element: <DataRequestAttachmentsComponent />
        },
        {
            path: 'RequestDetailsPrintView',
            element: <DataRequestPrintViewComponent />
        },
        {
            path: 'PublicDisclosures',
            element: <DisclosuresContainerComponent />
        },
        {
            path: 'AllCitations',
            element: <DataRequestCitationsComponent />
        },
        {
            path: 'ResearchEnvironment',
            element: <DataRequestResearchEnvironmentComponent />
        },
        {
            path: 'SignedAgreements',
            element: <DataRequestSignedDocumentsComponent />
        },
        {
            path: 'ResearchResults/:resultsRequestId?',
            element: <ResultRequestsFeature />
        },
        {
            index: true,
            path: 'Chat',
            element: <ChatRollupFeature />
        },
        {
            path: 'SafetyConcerns',
            element: <ReportSafetyConcernComponent />
        },
        {
            path: 'StatusUpdate',
            element: <DataRequestStatusUpdateComponent />
        },
        {
            path: 'PublishApprovedRequest',
            element: <DataRequestPublishApprovedComponent />
        },
        {
            path: 'ApprovedRequestsPublishing/:state',
            element: <PublishApprovedPreviewComponent />
        }
    ];

    const ctDataRequestByVivliIdPageRoutes = [
        {
            index: true,
            element: <ChatRollupFeature />
        },
        {
            path: 'Chat',
            element: <ChatRollupFeature />
        },
        {
            path: 'ResearchTeam',
            element: <DataRequestResearchTeamComponent />
        },
        {
            path: 'Studies',
            element: <DataRequestStudiesComponent />
        },
        {
            path: 'RequestHistory',
            element: <DataRequestHistoryComponent />
        },
        {
            path: 'Attachments',
            element: <DataRequestAttachmentsComponent />
        },
        {
            path: 'RequestDetailsPrintView',
            element: <DataRequestPrintViewComponent />
        },
        {
            path: 'PublicDisclosures',
            element: <DisclosuresContainerComponent />
        },
        {
            path: 'AllCitations',
            element: <DataRequestCitationsComponent />
        },
        {
            path: 'ResearchEnvironment',
            element: <DataRequestResearchEnvironmentComponent />
        },
        {
            path: 'SignedAgreements',
            element: <DataRequestSignedDocumentsComponent />
        },
        {
            path: 'ResearchResults/:resultsRequestId?',
            element: <ResultRequestsFeature />
        },
        {
            path: 'SafetyConcerns',
            element: <ReportSafetyConcernComponent />
        },
        {
            path: 'StatusUpdate',
            element: <DataRequestStatusUpdateComponent />
        },
        {
            path: 'PublishApprovedRequest',
            element: <DataRequestPublishApprovedComponent />
        },
        {
            path: 'ApprovedRequestsPublishing/:state',
            element: <PublishApprovedPreviewComponent />
        }
    ];

    const dataRequestPageRoutes = [
        {
            path: ':status/:requestId',
            element: dataRequestFeatureToUse,
            children: isAmr ? amrDataRequestRoutes : ctDataRequestPageRoutes
        },
        {
            path: ':requestId',
            element: dataRequestFeatureToUse,
            children: isAmr ? amrDataRequestRoutes : ctDataRequestPageRoutes
        },
        {
            path: 'vivliId/:requestId',
            element: <DataRequestByVivliIdFeature />,
            children: isAmr ? amrDataRequestRoutes : ctDataRequestByVivliIdPageRoutes
        }
    ];

    const dataRequestRoutes = [
        {
            path: '/adminDataRequests',
            children: dataRequestPageRoutes
        },
        {
            path: '/admindatarequests',
            children: dataRequestPageRoutes
        },
        {
            path: '/myDataRequestDetails',
            children: dataRequestDraftPageRoutes
        },
        {
            path: '/myDataRequestDetailsRO',
            children: dataRequestPageRoutes
        },
        {
            path: '/myDataRequests/:tab?',
            element: isAmr ? <MyDataRequestsFeature /> : <Navigate to="/admin/requests/" replace />
        }
    ];

    const portalRoutes = [
        {
            path: '/admin',
            element: <PortalFeature />,
            children: [
                {
                    index: true,
                    path: 'dashboard',
                    element: <DashboardFeatureRouteWrapper />
                },
                {
                    path: 'orgs',
                    children: [
                        {
                            index: true,
                            element: <OrganizationsFeature />
                        },
                        {
                            path: ':orgId/:tab?',
                            element: <OrganizationDetailsFeature />
                        }
                    ]
                },
                {
                    path: 'studies',
                    children: [
                        {
                            path: 'submitStudy/:studyId?/:tab?',
                            element: <SubmitStudyFeature />
                        },
                        {
                            path: 'annotateStudy/:studyId/:tab?',
                            element: <AnnotateStudyFeature />
                        },
                        {
                            path: ':studyId/:tab',
                            element: <AdminStudyFeature />
                        },
                        {
                            path: ':type?',
                            element: <StudiesFeature />
                        }
                    ]
                },
                {
                    path: 'users',
                    children: [
                        {
                            index: true,
                            element: <UsersFeature />
                        },
                        {
                            path: ':userId',
                            element: <UserDetailsFeature />
                        }
                    ]
                },
                {
                    path: 'vms/:vm?',
                    element: <ManageVmsFeature />
                },
                {
                    path: 'requests/:status?',
                    element: <DataRequestsFeature />
                },
                {
                    path: 'awaitingUpload',
                    element: <AwaitingUploadStudiesFeature />
                },
                {
                    path: 'bulkUploadStudies/:useSecureStorage?',
                    element: <BulkUploadStudiesFeature />
                },
                {
                    path: 'bulkUpdateStudies',
                    element: <BulkUpdateStudiesFeature />
                },
                {
                    path: 'report',
                    element: <ProtectedRoute requiredPermissions={[PermissionsEnum.DrActAdv, PermissionsEnum.ProviderActAdv]} />,
                    children: [{ index: true, element: <ReportFeature /> }]
                },
                {
                    path: 'study-submissions/*',
                    element: <ListingRequestsFeature />
                },
                {
                    path: 'adminConsole/:tab',
                    element: <AdminConsoleRequestsFeature />
                },
                {
                    path: 'enquiries/:tab?',
                    element: <EnquiresFeature />
                },
                {
                    path: 'enquiry/:id?',
                    element: <EnquiryFeature />
                }
            ]
        }
    ];

    const singleStudySubmissionRoutes = [
        {
            path: '/study-submissions/*',
            element: <ListingRequestsFeature />
        },
        {
            path: '/study-submission/:id?',
            element: <ListingRequestFeature />,
            children: [
                {
                    index: true,
                    path: 'InformationAboutYourTeam',
                    element: <InformationAboutYourTeamComponent />
                },
                {
                    path: 'YourOrganization',
                    element: <YourOrganizationComponent />
                },
                {
                    path: 'YourStudy',
                    element: <YourStudyComponent />
                },
                {
                    path: 'DataSharingSettings',
                    element: <DataSharingSettingsComponent />
                },
                {
                    path: 'Agreements',
                    element: <AgreementsComponent />
                },
                {
                    path: 'UploadData',
                    element: <UploadDataComponent />
                },
                {
                    path: 'History',
                    element: <ListingRequestHistoryComponent />
                },
                {
                    path: 'Chat',
                    element: <ListingRequestChatComponent />
                },
                {
                    path: 'StatusUpdate',
                    element: <ListingStatusUpdateComponent />
                }
            ]
        }
    ];

    const protectedRoutes = [
        {
            element: <ProtectedRoute />,
            children: [
                ...portalRoutes,
                ...datasetRoutes,
                ...studyRoutes,
                ...dataRequestRoutes,
                ...singleStudySubmissionRoutes,
                {
                    path: '/virtual-machine/:virtualMachineId/:dataRequestDoi/:userId?',
                    element: <VirtualMachinePortalFeature />
                },
                {
                    path: '/print-view',
                    children: [
                        {
                            path: ':data-request/:dataRequestId/:includeRiskScore',
                            element: <DataRequestPrintViewRenderComponent />
                        }
                    ]
                },
                {
                    path: '/disclosure/:dataRequestId/:disclosureId?/:tab?',
                    element: <DisclosureFeature />
                },
                {
                    path: '/disclosure/vivliId/:vivliId?/:tab?',
                    element: <DisclosureFromUrlFeature />
                }
            ]
        },
        {
            element: <ProtectedRoute />,
            children: [
                {
                    element: <ListingLandingViewComponent />,
                    children: [
                        {
                            path: '/study-submission',
                            element: <ListingRequestFeature />
                        }
                    ]
                }
            ]
        },
        {
            path: '/enquiries',
            element: <EnquiriesEntryWrapper />
        },
        {
            path: '/enquiries/link',
            element: <EnquiriesEntryWrapper />
        }
    ];

    return [
        {
            element: <PlatformInitWrapper />,
            children: [
                {
                    element: layoutWrapperToUse,
                    children: [
                        ...priorityRoutes,
                        ...publicRoutes,
                        ...protectedRoutes,
                        {
                            path: '*',
                            element: <NotFoundComponent />
                        }
                    ]
                }
            ]
        }
    ];
};
